import { assign, sendParent, setup } from 'xstate';
import type { RegisterHiveInput, ViewHivesQueryQuery } from '@/lib/gql/__generated__/graphql';
import { registerHiveActor, viewHivesActor } from './hive.actors';
import type { HiveEvent, HiveDomainEvent } from './events';

type HiveRow = ViewHivesQueryQuery['viewHives'][number];

type HiveContext = {
  hives: HiveRow[];
  error: string | null;
  pendingInput: Omit<RegisterHiveInput, 'hiveId'> | null;
  registeredHiveId: string | null;
};

export const hiveMachine = setup({
  types: {
    context: {} as HiveContext,
    events: {} as HiveEvent,
  },
  actors: {
    viewHives: viewHivesActor,
    registerHive: registerHiveActor,
  },
}).createMachine({
  id: 'hive',
  initial: 'idle',
  context: { hives: [], error: null, pendingInput: null, registeredHiveId: null },
  states: {
    idle: {
      on: { 'hive.load-hives': 'loading' },
    },
    loading: {
      invoke: {
        id: 'viewHives',
        src: 'viewHives',
        onDone: { target: 'ready', actions: assign(({ event }) => ({ hives: event.output.viewHives, error: null })) },
        onError: { target: 'error', actions: assign({ error: 'We could not load your hive register. Try again.' }) },
      },
    },
    ready: {
      on: {
        'hive.load-hives': 'loading',
        'hive.open-register': {
          actions: sendParent({ type: 'ui.overlay.open', name: 'registerHiveDialog' }),
        },
        'hive.register': {
          target: 'registering',
          actions: assign(({ event }) => ({ pendingInput: event.input, error: null })),
        },
      },
    },
    error: {
      on: { 'hive.load-hives': 'loading' },
    },
    registering: {
      invoke: {
        id: 'registerHive',
        src: 'registerHive',
        input: ({ context }) =>
          context.pendingInput ?? {
            apiaryName: '',
            hiveNumber: '',
            queenYear: 0,
            colonyStrain: '',
          },
        onDone: [
          {
            guard: ({ event }) => event.output.response.registerHive.success,
            target: 'loading',
            actions: [
              sendParent(
                ({ event }) => ({ type: 'hive.registered', hiveId: event.output.hiveId }) satisfies HiveDomainEvent,
              ),
              sendParent({ type: 'ui.overlay.close', name: 'registerHiveDialog' }),
              sendParent({
                type: 'ui.toast.push',
                toast: {
                  title: 'Hive registered',
                  description: 'Your hive is now in the national register.',
                  tone: 'success',
                },
              }),
            ],
          },
          {
            target: 'ready',
            actions: assign(({ event }) => ({
              error: event.output.response.registerHive.error?.message ?? 'The registry could not save this hive.',
            })),
          },
        ],
        onError: {
          target: 'ready',
          actions: [
            assign({ error: 'We could not reach the registry. Your details are still here.' }),
            sendParent({
              type: 'ui.toast.push',
              toast: {
                title: 'Registration failed',
                description: 'Check your connection and try again.',
                tone: 'error',
              },
            }),
          ],
        },
      },
    },
  },
});
